import React, { useState } from 'react';
import type { Issue } from '../../types/issue';
import type { EnterpriseAction } from '../../services/actionService';
import type { IssueMeasureRecord } from '../../services/issueMaspService';
import type { IssueEvidenceRecord } from '../../services/issueEvidenceService';
import { IssueEvidenceBoard } from './IssueEvidenceBoard';
import { isCauseConfirmed } from '../../utils/problemCause';
import {
  canConfirmEfficacy,
  efficacyAttempts,
  evaluateIssueClosure,
  readyForEfficacyCheck,
  type EfficacyAttempt,
} from '../../utils/efficacyCheck';
import { executionProgress, parseOptionalCost } from '../../utils/countermeasurePlan';
import {
  formatMeasureValue,
  presentMeasureComparison,
  type IssueMeasureView,
} from '../../utils/issueMasp';

interface VerificationWorkspaceProps {
  issue: Issue;
  actions: EnterpriseAction[];
  measures: IssueMeasureRecord[];
  evidence: IssueEvidenceRecord[];
  actorId?: string;
  actorRole?: string | null;
  closed: boolean;
  onAddAfter: (measureId: string, value: number, occurredOn: string) => Promise<void>;
  onConfirmEfficacy: (note: string) => Promise<void>;
  onRejectEfficacy: (note: string) => Promise<void>;
  onAddEvidence: (file: File, caption: string) => Promise<void>;
  onDeleteEvidence: (record: IssueEvidenceRecord) => Promise<void>;
  pending?: boolean;
  loadError?: boolean;
}

export const VerificationWorkspace: React.FC<VerificationWorkspaceProps> = ({
  issue,
  actions,
  measures,
  evidence,
  actorId,
  actorRole,
  closed,
  onAddAfter,
  onConfirmEfficacy,
  onRejectEfficacy,
  onAddEvidence,
  onDeleteEvidence,
  pending = false,
  loadError = false,
}) => {
  const direct = actions.filter((action) => action.problem_id === issue.id);
  const progress = executionProgress(direct);
  const confirmed = isCauseConfirmed(issue);
  const cause = issue.root_cause_analysis?.root_cause_description?.trim()
    || issue.root_cause?.trim()
    || '';
  const attempts = efficacyAttempts(issue.metadata);
  const ready = readyForEfficacyCheck(direct, issue.status);
  const closure = evaluateIssueClosure({
    actions: direct,
    attempts,
    closureReason: issue.metadata?.closure_reason,
    metadata: issue.metadata,
  });
  const permission = canConfirmEfficacy({
    severity: issue.severity,
    verifierId: issue.metadata?.verification_owner_id,
    problemOwnerId: issue.assigned_to,
    actorId,
    actorRole,
  });
  const covered = closure.case === 'B' && closure.canClose;
  const permissionReason = permission.allowed === false ? permission.reason : null;

  return (
    <div className="max-w-[960px] space-y-4">
      <section>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Causa confirmada</p>
        <p className="mt-0.5 text-[14px] font-medium text-slate-950">
          {confirmed && cause ? cause : 'A causa ainda não está consolidada.'}
        </p>
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Contramedidas</p>
        <p className="mt-0.5 text-[13px] text-slate-800">
          {progress.completed} de {progress.active} concluídas
        </p>
      </section>

      <section>
        <h3 className="text-[13px] font-semibold text-slate-900">Antes × depois</h3>
        {pending && measures.length === 0 ? (
          <p className="mt-1 text-[13px] text-slate-600">Carregando medidas.</p>
        ) : loadError && measures.length === 0 ? (
          <p className="mt-1 text-[13px] text-slate-600">Não foi possível carregar as medidas.</p>
        ) : measures.length === 0 ? (
          <p className="mt-1 text-[13px] text-slate-600">Nenhuma medida registrada.</p>
        ) : (
          <ul className="mt-1 border-t border-slate-200">
            {measures.map((measure) => (
              <MeasureRow
                key={measure.id}
                measure={measure}
                closed={closed}
                onAddAfter={onAddAfter}
              />
            ))}
          </ul>
        )}
      </section>

      <section>
        <h3 className="text-[13px] font-semibold text-slate-900">Eficácia</h3>
        <p className="mt-0.5 text-[12px] text-slate-500">
          {covered ? 'Eficaz para as contramedidas concluídas.' : 'Ação concluída não comprova o efeito.'}
        </p>
        {attempts.length > 0 ? (
          <ul className="mt-1 border-t border-slate-200">
            {attempts.map((attempt) => (
              <li key={`${attempt.at}-${attempt.result}`} className="border-b border-slate-100 py-1.5 text-[12px] text-slate-700">
                <span className="text-slate-900">{attempt.result === 'effective' ? 'Eficaz' : 'Não eficaz'}</span>
                {attempt.note ? ` · ${attempt.note}` : ''}
              </li>
            ))}
          </ul>
        ) : null}
        {!closed && ready && permission.allowed ? (
          <EfficacyDecision onConfirm={onConfirmEfficacy} onReject={onRejectEfficacy} />
        ) : (
          <p className="mt-1 text-[13px] text-slate-600">
            {!ready ? (closure.reason ?? 'A verificação cobre as contramedidas concluídas.') : null}
            {ready ? permissionReason : null}
          </p>
        )}
      </section>

      <IssueEvidenceBoard
        records={evidence}
        editable={!closed}
        onAdd={onAddEvidence}
        onDelete={onDeleteEvidence}
        emptyLabel={pending ? 'Carregando evidências.' : loadError ? 'Não foi possível carregar as evidências.' : 'Nenhuma evidência de verificação.'}
      />
    </div>
  );
};

function MeasureRow({
  measure,
  closed,
  onAddAfter,
}: {
  measure: IssueMeasureRecord;
  closed: boolean;
  onAddAfter: VerificationWorkspaceProps['onAddAfter'];
}) {
  const view: IssueMeasureView = measure;
  const row = presentMeasureComparison(view);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [occurredOn, setOccurredOn] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = parseOptionalCost(value);
    if (parsed.error || parsed.amount == null) {
      setError(parsed.error ?? 'Informe o valor medido.');
      return;
    }
    if (!occurredOn) {
      setError('Informe a data da medição.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onAddAfter(measure.id, parsed.amount, occurredOn);
      setOpen(false);
      setValue('');
      setOccurredOn('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível registrar a medição.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="border-b border-slate-100 py-2 text-[12px] text-slate-800">
      <p className="text-[13px] text-slate-900">{row.name} <span className="text-slate-500">{row.unit}</span></p>
      <p className="text-slate-600">
        Antes {formatMeasureValue(row.before)} · Meta {formatMeasureValue(row.target)} · Depois {formatMeasureValue(row.after)} · {row.result}
      </p>
      {!closed ? (
        open ? (
          <form onSubmit={(event) => void save(event)} className="mt-1 flex flex-wrap items-center gap-2">
            <input
              aria-label={`Depois de ${row.name}`}
              value={value}
              onChange={(event) => setValue(event.target.value)}
              inputMode="decimal"
              className="h-8 w-24 rounded border border-slate-300 px-2 text-[13px]"
            />
            <input
              aria-label={`Data de ${row.name}`}
              type="date"
              value={occurredOn}
              onChange={(event) => setOccurredOn(event.target.value)}
              className="h-8 rounded border border-slate-300 px-2 text-[13px]"
            />
            <button type="submit" disabled={saving} className="font-medium text-slate-800 hover:underline disabled:opacity-50">
              {saving ? 'Salvando...' : 'Registrar'}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-slate-500 hover:underline">Fechar</button>
            {error ? <span className="text-red-700">{error}</span> : null}
          </form>
        ) : (
          <button type="button" onClick={() => setOpen(true)} className="mt-0.5 font-medium text-slate-700 hover:underline">
            Registrar depois
          </button>
        )
      ) : null}
    </li>
  );
}

function EfficacyDecision({
  onConfirm,
  onReject,
}: {
  onConfirm: (note: string) => Promise<void>;
  onReject: (note: string) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<'effective' | 'ineffective' | null>(null);

  const run = async (result: EfficacyAttempt['result']) => {
    if (!note.trim()) {
      setError('A observação é obrigatória.');
      return;
    }
    setBusy(result === 'effective' ? 'effective' : 'ineffective');
    setError('');
    try {
      if (result === 'effective') await onConfirm(note.trim());
      else await onReject(note.trim());
      setNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível registrar a verificação.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2 max-w-md">
      <label className="block text-[12px] text-slate-600">
        Observação
        <textarea
          aria-label="Observação da eficácia"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-[13px]"
        />
      </label>
      {error ? <p className="mt-1 text-[12px] text-red-700">{error}</p> : null}
      <div className="mt-1 flex gap-3 text-[12px]">
        <button type="button" disabled={busy != null} onClick={() => void run('effective')} className="font-medium text-slate-800 hover:underline disabled:opacity-50">
          {busy === 'effective' ? 'Salvando...' : 'Eficaz'}
        </button>
        <button type="button" disabled={busy != null} onClick={() => void run('ineffective')} className="font-medium text-slate-800 hover:underline disabled:opacity-50">
          {busy === 'ineffective' ? 'Salvando...' : 'Não eficaz'}
        </button>
      </div>
    </div>
  );
}
